/**
 * useGridPointer — un sol router de Pointer Events per a la graella del calendari.
 *
 * Runbook 7, F2 (decisions 4-8). Substitueix els antics handlers de ratolí i de tacte de
 * `CalendarGrid`: a cada gest decideix si és `scroll`, `tap`, `move-row` o `resize`.
 * La decisió és la funció pura `classifyGesture` (amb tests); el hook només hi posa l'estat.
 *
 * - **Ratolí:** arrossegar el fons fa scroll en tots dos eixos (×1,5, com abans).
 * - **Tàctil / llapis:** bloqueig de direcció. El contenidor porta `touch-action: pan-y`: el
 *   navegador fa el scroll vertical natiu (i ens envia `pointercancel`), i l'horitzontal el fem
 *   nosaltres. És l'equivalent en Pointer Events del `touchmove` amb `preventDefault()` d'abans.
 * - **`move-row` i `resize`** són ganxos (`moveRow`, `resize`) que omple F3. Mentre no n'hi ha
 *   cap, el gest cau a `scroll`, perquè arrossegar una reserva amb el ratolí faci el d'abans.
 *
 * Qui és què ho diuen atributs del DOM, no props: el bloc d'una reserva porta
 * `data-grid-target="reservation"` i `data-reservation-id`, i la nansa de la vora (F3)
 * `data-grid-target="edge"`. Tota la resta és `cell`.
 *
 * El comportament es decideix pel `pointerType`, mai per l'amplada de la pantalla.
 *
 * Bloqueig a l'habitació (`plans/reservation-move-lock.md` §4 F.3): una card bloquejada no porta
 * `data-grid-target` (no s'agafa) però sí `data-move-lock="{motiu}"`. Si el ratolí hi comença un
 * arrossegament, la graella fa scroll com a qualsevol fons i surt un toast «Bloquejada: {motiu}».
 */

import { useCallback, useRef, useState, type RefObject } from 'react';
import { toast } from 'sonner';

// ============================================
// TYPES
// ============================================

export type GridPointerType = 'mouse' | 'touch' | 'pen';
export type GestureStart = 'reservation' | 'edge' | 'cell';
export type GridGesture = 'scroll' | 'tap' | 'move-row' | 'resize';
/** `calendarV2_settings.dragMode` (el desa F3). `off` = mai s'arrossega cap reserva. */
export type DragMode = 'off' | 'mouse';

export interface ClassifyGestureInput {
  pointerType: GridPointerType;
  startedOn: GestureStart;
  movedPx: number;
  dragMode: DragMode;
}

export interface GridPoint {
  x: number;
  y: number;
}

/** El que reben els ganxos de F3 a cada pas d'un gest `move-row` o `resize`. */
export interface GestureContext {
  gesture: 'move-row' | 'resize';
  pointerType: GridPointerType;
  pointerId: number;
  startedOn: GestureStart;
  reservationId: string | null;
  /** Coordenades de client (`clientX/Y`) en prémer i ara. */
  start: GridPoint;
  current: GridPoint;
  /** Scroll del contenidor en prémer: F3 el necessita per convertir el punter en fila. */
  startScroll: { left: number; top: number };
}

export interface GestureHooks {
  onStart?: (ctx: GestureContext) => void;
  onMove?: (ctx: GestureContext) => void;
  onEnd?: (ctx: GestureContext) => void;
  /** Esc, `pointercancel` o `cancel()`: res s'escriu. */
  onCancel?: (ctx: GestureContext) => void;
}

export interface LastGesture {
  pointerType: GridPointerType | null;
  wasTap: boolean;
}

export interface UseGridPointerOptions {
  containerRef: RefObject<HTMLElement | null>;
  dragMode?: DragMode;
  moveRow?: GestureHooks;
  resize?: GestureHooks;
}

export interface GridPointerHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface UseGridPointerResult {
  handlers: GridPointerHandlers;
  /** `touch-action` per al contenidor (bloqueig de direcció al tàctil). */
  touchAction: string;
  /** L'últim gest acabat: tipus de punter i si era un tap. Es llegeix en un `click`. */
  getLastGesture: () => LastGesture;
  /** `true` des que un gest deixa de ser un tap fins que s'acaba (el HoverCard no s'obre). */
  isDragging: boolean;
  /** Avorta el gest en curs (F3: `Esc`). */
  cancel: () => void;
}

// ============================================
// PURE HELPERS
// ============================================

/** Més moviment que això entre prémer i deixar anar ja és arrossegar, no un tap. */
export const TAP_MAX_MOVE_PX = 5;
/** Factor del scroll arrossegant, el mateix d'abans. */
export const DRAG_SCROLL_FACTOR = 1.5;
export const GRID_TARGET_ATTR = 'data-grid-target';
export const GRID_RESERVATION_ID_ATTR = 'data-reservation-id';
/** El posa la card d'una reserva bloquejada a l'habitació, amb el motiu (`ReservationCell`). */
export const GRID_MOVE_LOCK_ATTR = 'data-move-lock';

/**
 * Tap o arrossegament? El `click` arriba igualment a la cel·la després d'arrossegar la graella
 * amb el ratolí, i obriria el formulari de creació sense voler. `true` = és un tap.
 */
export function isTapGesture(
  start: GridPoint | null,
  end: GridPoint | null,
  maxMovePx: number = TAP_MAX_MOVE_PX,
): boolean {
  if (!start || !end) return true;
  return Math.hypot(end.x - start.x, end.y - start.y) <= maxMovePx;
}

/**
 * Què és aquest gest? Funció pura.
 *
 * - Fins a `TAP_MAX_MOVE_PX` de moviment, qualsevol punter fa `tap` (a una cel·la buida obre
 *   el formulari de nova reserva; a una reserva, la fitxa o el cos d'informació).
 * - Tàctil i llapis: mai arrosseguen reserves en aquesta versió (decisió 7). Si es mouen, és
 *   `scroll`; si no, `tap`, sigui on sigui i amb qualsevol `dragMode`.
 * - Ratolí: el fons fa `scroll`; el cos d'una reserva, `move-row`; la vora, `resize`. Amb
 *   `dragMode: 'off'`, tot és `scroll`.
 */
export function classifyGesture({
  pointerType,
  startedOn,
  movedPx,
  dragMode,
}: ClassifyGestureInput): GridGesture {
  if (movedPx <= TAP_MAX_MOVE_PX) return 'tap';
  if (pointerType !== 'mouse') return 'scroll';
  if (dragMode === 'off') return 'scroll';
  if (startedOn === 'reservation') return 'move-row';
  if (startedOn === 'edge') return 'resize';
  return 'scroll';
}

export function normalizePointerType(pointerType: string): GridPointerType {
  return pointerType === 'touch' || pointerType === 'pen' ? pointerType : 'mouse';
}

/** On ha començat el gest i, si és una reserva, quina. */
export function resolveGestureTarget(target: EventTarget | Pick<Element, 'closest'> | null): {
  startedOn: GestureStart;
  reservationId: string | null;
} {
  // Sense `instanceof Element`: així es prova sense DOM (els tests van amb environment node).
  const closest = target && 'closest' in target ? target.closest.bind(target) : null;
  const el = closest ? closest(`[${GRID_TARGET_ATTR}]`) : null;
  const kind = el?.getAttribute(GRID_TARGET_ATTR);
  if (kind !== 'reservation' && kind !== 'edge') return { startedOn: 'cell', reservationId: null };
  const owner = el?.closest(`[${GRID_RESERVATION_ID_ATTR}]`);
  return { startedOn: kind, reservationId: owner?.getAttribute(GRID_RESERVATION_ID_ATTR) ?? null };
}

/** El motiu, si el gest comença sobre una card bloquejada; si no, `null`. Sense DOM, com l'anterior. */
export function resolveMoveLockReason(target: EventTarget | Pick<Element, 'closest'> | null): string | null {
  const closest = target && 'closest' in target ? target.closest.bind(target) : null;
  const el = closest ? closest(`[${GRID_MOVE_LOCK_ATTR}]`) : null;
  return el?.getAttribute(GRID_MOVE_LOCK_ATTR) || null;
}

/** Toast en intentar arrossegar una bloquejada: només ratolí, amb l'arrossegament actiu i des del cos. */
export function blockedDragReason(
  g: { pointerType: GridPointerType; startedOn: GestureStart; moveLockReason: string | null },
  dragMode: DragMode,
): string | null {
  if (g.pointerType !== 'mouse' || dragMode === 'off' || g.startedOn !== 'cell') return null;
  return g.moveLockReason;
}

// ============================================
// HOOK
// ============================================

interface ActiveGesture {
  pointerId: number;
  pointerType: GridPointerType;
  startedOn: GestureStart;
  reservationId: string | null;
  start: GridPoint;
  current: GridPoint;
  startScroll: { left: number; top: number };
  /** `null` mentre encara pot ser un tap. */
  gesture: GridGesture | null;
  /** Només tàctil/llapis: la direcció queda fixada al primer moviment de més de 5 px. */
  direction: 'horizontal' | 'vertical' | null;
  /** Motiu del bloqueig, si ha començat sobre una card bloquejada. */
  moveLockReason: string | null;
}

export function useGridPointer({
  containerRef,
  dragMode = 'mouse',
  moveRow,
  resize,
}: UseGridPointerOptions): UseGridPointerResult {
  const activeRef = useRef<ActiveGesture | null>(null);
  const lastGestureRef = useRef<LastGesture>({ pointerType: null, wasTap: true });
  const [isDragging, setIsDragging] = useState(false);

  // Els ganxos canvien a cada render; els llegim d'una ref per no refer els handlers.
  const hooksRef = useRef({ moveRow, resize, dragMode });
  hooksRef.current = { moveRow, resize, dragMode };

  const hooksFor = (gesture: GridGesture | null): GestureHooks | undefined => {
    if (gesture === 'move-row') return hooksRef.current.moveRow;
    if (gesture === 'resize') return hooksRef.current.resize;
    return undefined;
  };

  const contextOf = (g: ActiveGesture): GestureContext => ({
    gesture: g.gesture === 'resize' ? 'resize' : 'move-row',
    pointerType: g.pointerType,
    pointerId: g.pointerId,
    startedOn: g.startedOn,
    reservationId: g.reservationId,
    start: g.start,
    current: g.current,
    startScroll: g.startScroll,
  });

  const finish = useCallback((cancelled: boolean) => {
    const g = activeRef.current;
    if (!g) return;
    activeRef.current = null;
    lastGestureRef.current = { pointerType: g.pointerType, wasTap: g.gesture === null && !cancelled };
    const hooks = hooksFor(g.gesture);
    if (hooks) {
      if (cancelled) hooks.onCancel?.(contextOf(g));
      else hooks.onEnd?.(contextOf(g));
    }
    const el = containerRef.current;
    if (el?.hasPointerCapture?.(g.pointerId)) el.releasePointerCapture(g.pointerId);
    setIsDragging(false);
  }, [containerRef]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const el = containerRef.current;
      if (!el) return;
      // Els portals (HoverCard, overlay) propaguen pel arbre de React: només compta el DOM de la graella.
      if (!(e.target instanceof Node) || !el.contains(e.target)) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (activeRef.current) finish(true);

      const { startedOn, reservationId } = resolveGestureTarget(e.target);
      const point = { x: e.clientX, y: e.clientY };
      activeRef.current = {
        pointerId: e.pointerId,
        pointerType: normalizePointerType(e.pointerType),
        startedOn,
        reservationId,
        start: point,
        current: point,
        startScroll: { left: el.scrollLeft, top: el.scrollTop },
        gesture: null,
        direction: null,
        moveLockReason: resolveMoveLockReason(e.target),
      };
      lastGestureRef.current = { pointerType: activeRef.current.pointerType, wasTap: true };
    },
    [containerRef, finish],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = activeRef.current;
      const el = containerRef.current;
      if (!g || !el || e.pointerId !== g.pointerId) return;
      g.current = { x: e.clientX, y: e.clientY };
      const dx = g.current.x - g.start.x;
      const dy = g.current.y - g.start.y;

      if (g.gesture === null) {
        const decided = classifyGesture({
          pointerType: g.pointerType,
          startedOn: g.startedOn,
          movedPx: Math.hypot(dx, dy),
          dragMode: hooksRef.current.dragMode,
        });
        if (decided === 'tap') return;
        const lockReason = blockedDragReason(g, hooksRef.current.dragMode);
        if (lockReason) toast.info(`Bloquejada: ${lockReason}`, { id: 'move-lock-drag' });
        // Sense ganxo de F3, arrossegar una reserva fa el d'abans: scroll.
        g.gesture = hooksFor(decided) ? decided : 'scroll';
        if (g.pointerType !== 'mouse') {
          g.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
        // Capturar només ara: si es capturés en prémer, el `click` d'un tap aniria al contenidor.
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          // el punter ja no hi és (p. ex. `pointercancel` pel camí): res a fer
        }
        setIsDragging(true);
        hooksFor(g.gesture)?.onStart?.(contextOf(g));
      }

      if (g.gesture === 'scroll') {
        if (g.pointerType === 'mouse') {
          el.scrollLeft = g.startScroll.left - dx * DRAG_SCROLL_FACTOR;
          el.scrollTop = g.startScroll.top - dy * DRAG_SCROLL_FACTOR;
        } else if (g.direction === 'horizontal') {
          el.scrollLeft = g.startScroll.left - dx * DRAG_SCROLL_FACTOR;
        }
        // Vertical al tàctil: el fa el navegador (`touch-action: pan-y`).
        return;
      }
      hooksFor(g.gesture)?.onMove?.(contextOf(g));
    },
    [containerRef],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = activeRef.current;
      if (!g || e.pointerId !== g.pointerId) return;
      g.current = { x: e.clientX, y: e.clientY };
      // Per si no hi ha hagut cap `pointermove` (alguns llapis): el tap es mesura igual que abans.
      if (g.gesture === null && !isTapGesture(g.start, g.current)) g.gesture = 'scroll';
      finish(false);
    },
    [finish],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = activeRef.current;
      if (!g || e.pointerId !== g.pointerId) return;
      // Al tàctil, el navegador ha agafat el scroll vertical: no és un tap.
      if (g.gesture === null) g.gesture = 'scroll';
      finish(true);
    },
    [finish],
  );

  const cancel = useCallback(() => finish(true), [finish]);
  const getLastGesture = useCallback(() => lastGestureRef.current, []);

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    touchAction: 'pan-y pinch-zoom',
    getLastGesture,
    isDragging,
    cancel,
  };
}
